import { API_BASE_URL } from '../../../../api';
import React, { useContext, useEffect, useRef, useState } from "react";
import './showLocations.style.scss';
import { AccountContext } from "../../../../Contexts/main.context";
import EditCitationModal from "../EditCitations/EditCitation.component";

const citationDate = (citation) => {
     const year = Number(citation.citationYear);
     const month = Number(citation.citationMonth);
     const dayText = String(citation.citationDay ?? '').trim();
     const day = Number(dayText);
     const legacyDate = citation.date ? new Date(citation.date) : null;
     const fallback = legacyDate && Number.isFinite(legacyDate.getTime()) ? legacyDate : null;
     if (!Number.isInteger(year) || year < 1 || year > 9999 ||
          !Number.isInteger(month) || month < 1 || month > 12) return fallback;

     const date = new Date(0);
     date.setUTCFullYear(year, month - 1, 1);
     if (dayText !== '' && day !== 0) {
          if (!Number.isInteger(day) || day < 1 || day > 31) return fallback;
          date.setUTCDate(day);
          if (date.getUTCMonth() !== month - 1) return fallback;
     }
     return date;
};

const formatCitationDate = (citation) => {
     const date = citationDate(citation);
     if (!date) return citation.date || 'Not Set';
     if (!Number(citation.citationYear) || !Number(citation.citationMonth)) return citation.date || 'Not Set';
     return date.toLocaleDateString('en-US', {
          year: 'numeric', month: 'long',
          ...(Number(citation.citationDay) ? { day: 'numeric' } : {}),
          timeZone: 'UTC',
     });
};

const urlCheckDate = (value) => {
     if (!value) return null;
     // MySQL DATETIME is stored in UTC; API responses use ISO timestamps.
     const date = new Date(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
          ? value.replace(' ', 'T') + 'Z' : value);
     return Number.isFinite(date.getTime()) ? date : null;
};
const formatCheckTime = (date) => date.toLocaleString(undefined, {
     year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit', timeZoneName: 'short',
});

const CitationsList = ({ citations = [] }) => {
     const context = useContext(AccountContext);
     const { activeClient, updateMasked, myAccount } = context || {};
     const isSuperAdmin = [true, 1, '1'].includes(myAccount?.superAdmin);
     const citationActionsDisabled = !isSuperAdmin;
     const [activeCitations, setActiveCitations] = useState([]);
     const [statusFilter, setStatusFilter] = useState('active');
     const urlStatusLabels = { unverified: 'Not verified', ok: 'Working', not_found: '404 Not Found', missing: 'No URL', error: 'Could not verify' };
     const normalizeStatus = (status) => String(status || '').trim().toLowerCase();
     const filteredCitations = activeCitations
          .filter(citation => normalizeStatus(citation.citationStatus) === statusFilter)
          .sort((a, b) => (citationDate(b)?.getTime() ?? -Infinity) - (citationDate(a)?.getTime() ?? -Infinity));
     const activeCitationCount = activeCitations.filter(citation => normalizeStatus(citation.citationStatus) === 'active').length;
     const [editingCitation, setEditingCitation] = useState(null);
     const [copyFeedback, setCopyFeedback] = useState(null);
     const copyTimer = useRef(null);
     const copyRequest = useRef(0);
     const [urlChecks, setUrlChecks] = useState({});
     const [accountVerifiedAt, setAccountVerifiedAt] = useState(null);
     const [verificationProgress, setVerificationProgress] = useState(null);
     const verificationRequest = useRef(null);
     const citationUrl = (citation) => String(citation.CitationLink || '').trim();
     const hasCitationUrl = (citation) => citationUrl(citation) !== '' && citationUrl(citation) !== '-';

     const checkFor = (citation) => {
          const check = urlChecks[citation.ID];
          return check?.link === citationUrl(citation) ? check : { status: citation.URLStatus || 'unverified' };
     };
     const checkedCitations = activeCitations.filter(citation => ['ok', 'not_found', 'missing', 'error'].includes(checkFor(citation).status));
     const verificationCounts = checkedCitations.reduce((counts, citation) => {
          counts[checkFor(citation).status]++;
          return counts;
     }, { ok: 0, not_found: 0, missing: 0, error: 0 });

     const lastUrlCheck = urlCheckDate(accountVerifiedAt);

     const finishDelete = (ID) => {
          setActiveCitations(current => current.filter(citation => String(citation.ID) !== String(ID)));
          updateMasked(false);
          setEditingCitation(null);
     };

     const getCitations = async () => {
          verificationRequest.current?.abort();
          verificationRequest.current = null;
          setUrlChecks({});
          setAccountVerifiedAt(activeClient.URLVerifiedAt || null);
          setVerificationProgress(null);
          const sendData = { clientID: activeClient.ID, script: 'getcitations' }
          try {
               const response = await fetch(API_BASE_URL + 'special.php', {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(sendData),
               });

               const data = await response.json();
               const verifiedAt = response.headers?.get('X-URL-Verified-At');
               if (verifiedAt !== undefined) setAccountVerifiedAt(verifiedAt || null);
               setActiveCitations(data);
               if (data) {
                    setActiveCitations(data);
               } else {
                    alert("Citations Not Formatted Correctly");
               }
          } catch (error) {
               alert("Error Retrieving Citations");
          }
     }

     useEffect(() => {
          setStatusFilter('active');
          setCopyFeedback(null);
          clearTimeout(copyTimer.current);
          copyRequest.current++;
          setLinkPressed('');
          getCitations();
          return () => {
               clearTimeout(copyTimer.current);
               copyRequest.current++;
               verificationRequest.current?.abort();
               verificationRequest.current = null;
          };
           // eslint-disable-next-line
     }, [activeClient])


     const [linkPressed, setLinkPressed] = useState('');

     const copyLink = async (citation) => {
          const link = citationUrl(citation);
          const url = link.startsWith('//') ? `https:${link}` : /^[a-z][a-z0-9+.-]*:/i.test(link) ? link : `https://${link}`;
          if (!/^https?:\/\//i.test(url)) return;
          setLinkPressed(citation.ID);
          const request = ++copyRequest.current;
          clearTimeout(copyTimer.current);
          setCopyFeedback(null);
          try {
               if (navigator.clipboard?.writeText) {
                    await navigator.clipboard.writeText(url);
               } else {
                    const field = document.createElement('textarea');
                    field.value = url;
                    field.style.position = 'fixed';
                    field.style.opacity = '0';
                    const focused = document.activeElement;
                    document.body.appendChild(field);
                    try {
                         field.select();
                         if (!document.execCommand('copy')) throw new Error('Copy failed');
                    } finally {
                         field.remove();
                         focused?.focus();
                    }
               }
               if (request !== copyRequest.current) return;
               setCopyFeedback({ ID: citation.ID, message: 'Copied' });
          } catch (error) {
               if (request !== copyRequest.current) return;
               setCopyFeedback({ ID: citation.ID, message: 'Copy failed' });
          }
          copyTimer.current = setTimeout(() => setCopyFeedback(null), 1800);
     };

     const finishEdit = () => {
          updateMasked(false); // ✅ remove screen mask
          setEditingCitation(null);
          getCitations();
     };


     const openEditModal = (citation) => {
          updateMasked(true); // ✅ mask the screen
          setEditingCitation(citation);
     };

     const verifyUrls = async () => {
          if (verificationRequest.current || !activeCitations.length) return;
          const controller = new AbortController();
          verificationRequest.current = controller;
          const results = {};
          const queue = [...activeCitations];
          setUrlChecks({ ...results });
          let completed = 0;
          setVerificationProgress({ completed, total: activeCitations.length, running: true });
          let next = 0;
          const worker = async () => {
               while (next < queue.length && !controller.signal.aborted) {
                    const citation = queue[next++];
                    const link = citationUrl(citation);
                    let result;
                    try {
                         const response = await fetch(API_BASE_URL + 'special.php', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ script: 'verifyCitationUrl', ID: citation.ID, clientID: activeClient.ID, link }),
                              signal: controller.signal,
                         });
                         result = await response.json();
                         if (!response.ok || result.saved !== true || !['ok', 'not_found', 'error', 'missing'].includes(result.status)) {
                              throw new Error(result.message || 'Unable to verify URL.');
                         }
                    } catch (error) {
                         if (controller.signal.aborted) return;
                         result = { status: 'error', saved: false, message: error.message || 'Could not verify URL. Please try again.' };
                    }
                    if (verificationRequest.current !== controller || controller.signal.aborted) return;
                    if (result.saved) {
                         if (result.checkedAt) setAccountVerifiedAt(current =>
                              !urlCheckDate(current) || urlCheckDate(result.checkedAt) > urlCheckDate(current) ? result.checkedAt : current);
                         setActiveCitations(current => current.map(item => String(item.ID) === String(citation.ID) && citationUrl(item) === link
                              ? { ...item, URLStatus: result.status, URLCheckedAt: result.checkedAt || item.URLCheckedAt } : item));
                    }
                    results[citation.ID] = { ...result, link };
                    completed++;
                    setUrlChecks({ ...results });
                    setVerificationProgress({ completed, total: activeCitations.length, running: true });
               }
          };
          await Promise.all(Array.from({ length: Math.min(3, queue.length) }, worker));
          if (verificationRequest.current !== controller || controller.signal.aborted) return;
          verificationRequest.current = null;
          const counts = Object.values(results).reduce((totals, result) => {
               totals[result.status]++;
               return totals;
          }, { ok: 0, not_found: 0, missing: 0, error: 0 });
          setVerificationProgress({ completed, total: activeCitations.length, running: false, counts });
     };

     const checkIndex = async (ID) => {
          const checkRecord = activeCitations.find(needle => needle.ID === ID);
          console.log(checkRecord);
          if (!checkRecord || !checkRecord.CitationLink) return;

          try {
               const res = await fetch(API_BASE_URL + 'special.php', {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                         script: "checkIndex",
                         link: checkRecord.CitationLink
                    }),
               });

               const result = await res.json();
               const whatResult = result.indexed === true;
               alert(`🔍 ${checkRecord.CitationLink} is ${whatResult ? 'indexed ✅' : 'not indexed ❌'}`);

          } catch (error) {
               console.error("Index check failed:", error);
          }
     };



     return (
          <>
               <div className="col-46 offset-1 bg-light">
                    <div className="justify-content-center p-4 fs-5 fw-bold mb-2 alert-dark d-flex">
                         <div>
                             New Current Location - {activeClient.companyName} - {activeClient.theLocation}
                         </div>
                         <div className="ps-3 text-purple fw-bold">[{activeCitationCount} Citations]</div>
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-3 px-4 pb-3">
                         <div className="btn-group" role="group" aria-label="Citation status">
                              {['active', 'pending', 'potential'].map(status => (
                                   <button
                                        key={status}
                                        type="button"
                                        className={`btn ${statusFilter === status ? 'btn-primary' : 'btn-outline-primary'}`}
                                        aria-pressed={statusFilter === status}
                                        onClick={() => setStatusFilter(status)}
                                   >
                                        {status.charAt(0).toUpperCase() + status.slice(1)}
                                   </button>
                              ))}
                         </div>
                         <span>{filteredCitations.length} shown</span>
                         <div className="ms-auto d-flex flex-wrap align-items-center gap-3">
                         <button
                              type="button"
                              className="btn btn-primary"
                              disabled={citationActionsDisabled || !activeCitations.length || verificationProgress?.running}
                              onClick={verifyUrls}
                         >
                              {verificationProgress?.running ? `Verifying ${verificationProgress.completed}/${verificationProgress.total}…` : 'Verify URL'}
                         </button>
                         <span className="text-muted" aria-live="polite" title="Last saved URL verification run for this location">
                              Last verified: {lastUrlCheck ? formatCheckTime(lastUrlCheck) : 'Not checked yet'}
                         </span>
                         </div>
                    </div>
                    {verificationProgress && (
                         <div className="px-4 pb-3" role="status" aria-live="polite">
                              {verificationProgress.running
                                   ? `Checking all citations for this location: ${verificationProgress.completed} of ${verificationProgress.total}.`
                                   : `${verificationCounts.ok} working, ${verificationCounts.not_found} returned 404, ${verificationCounts.missing} without a URL, ${verificationCounts.error} could not be verified.`}
                         </div>
                    )}
                    <div className="citations-list">
                         <div className="d-flex bg-navy text-lemon fs-6 p-4">
                              <div className="col-10">Source</div>
                              <div className="col-1 text-center">S</div>
                              <div className="col-4 text-center">Status</div>
                              <div className="col-4 text-center">Date</div>
                              <div className="col-9 text-center">Value</div>
                              <div className="col-3 text-center">Name</div>
                              <div className="col-3 text-center">Address</div>
                              <div className="col-3 text-center">Phone</div>
                              <div className="col-3 text-center">Postal</div>
                              {/* <div className="col-5  d-flex gap-1">
                                   <div className="col-24 pe-1 text-center">Link</div>
                                   <div className="col-22 text-center">More</div>
                              </div> */}
                         </div>
                         {filteredCitations.length === 0 && <div className="p-4">No {statusFilter} citations for this location.</div>}
                         {filteredCitations.map((citation, index) => {
                              const statusClassMap = {
                                   live: 'bg-success',
                                   edit: 'btn-warning',
                                   dead: 'bg-danger',
                                   default: 'btn-dark'
                              };

                              const status = citation.citationStatus?.toLowerCase();
                              const statusClass = statusClassMap[status] || statusClassMap.default;
                              const urlCheck = checkFor(citation);
                              const hasUrl = hasCitationUrl(citation);
                              const linkClass = !hasUrl ? 'btn-secondary' : urlCheck?.status === 'not_found' ? 'btn-danger' : urlCheck?.status === 'error' ? 'btn-warning' : 'btn-primary';
                              const linkTitle = !hasUrl ? 'No citation URL provided.' : urlCheck?.message || urlStatusLabels[urlCheck.status];

                              return (
                                   <div key={citation.ID || index}>
                                        <div className={`d-flex px-4 py-2 justify-content-start align-items-center border-bottom border-2 border-navy ${citation.ID === linkPressed ? 'citation-selected' : ''}`}>
                                             <div className="col-10">
                                                  <div>{citation.Domain}</div>
                                                  <small className="d-block">URL Status: {urlStatusLabels[urlCheck.status] || 'Not verified'}</small>
                                                  {urlCheckDate(citation.URLCheckedAt) && <small className="d-block">Last checked: {formatCheckTime(urlCheckDate(citation.URLCheckedAt))}</small>}
                                                  {urlCheck.saved === false && <small className="d-block text-danger">Status was not saved. Retry verification.</small>}
                                             </div>
                                             <div className="col-1 text-center fs-7">{citation.source === 'Monday' ? 'M' : 'B'}</div>
                                             <div className="col-4">
                                                  <div className={`text-center text-light badge col-40 offset-4 p-2 ${statusClass}`}>
                                                       <div className="text-uppercase">
                                                            {citation.citationStatus}
                                                       </div>
                                                  </div>
                                             </div>
                                             <div className="col-4 text-center">{formatCitationDate(citation)}</div>
                                             <div className="col-9 text-center">
                                                  {['', '-', 'unknown'].includes(citation.CitationValue?.toString().toLowerCase())
                                                       ? '?'
                                                       : citation.CitationValue}
                                             </div>

                                             <div className={`col-3 text-center ${citation.BusinessNameIssue?.toLowerCase() === 'yes' ? 'alert-danger' : ''}`}>
                                                  {citation.BusinessNameIssue?.toLowerCase() === 'yes' ? 'Yes' : '-'}
                                             </div>

                                             <div className={`col-3 text-center ${citation.Address_Issue?.toLowerCase() === 'yes' ? 'alert-danger' : ''}`}>{citation.Address_Issue ? 'Yes' : '-'}</div>
                                             <div className={`col-3 text-center ${citation.PhoneNumberIssue?.toLowerCase() === 'yes' ? 'alert-danger' : ''}`}>{citation.PhoneNumberIssue ? 'Yes' : '-'}</div>
                                             <div className={`col-3 text-center ${citation.PostalCodeIssue?.toLowerCase() === 'yes' ? 'alert-danger' : ''}`}>{citation.PostalCodeIssue ? 'Yes' : '-'}</div>
                                             <div className="col-7 citation-actions">
                                                  <button
                                                            type="button"
                                                            className={`btn ${copyFeedback?.ID === citation.ID && copyFeedback.message === 'Copied' ? 'btn-success' : linkClass}`}
                                                            title={linkTitle}
                                                            aria-label={`Link for ${citation.Domain}: ${linkTitle}`}
                                                            disabled={!hasUrl}
                                                            onClick={() => copyLink(citation)}
                                                            aria-live="polite"
                                                       >{copyFeedback?.ID === citation.ID ? copyFeedback.message : 'Link'}</button>
                                                  <button type="button" className="btn btn-danger" disabled={citationActionsDisabled} onClick={() => checkIndex(citation.ID)}>Index</button>
                                                  <button
                                                       type="button"
                                                       className="btn btn-warning"
                                                       disabled={citationActionsDisabled}
                                                       onClick={() => openEditModal(citation)}
                                                  >
                                                       Edit
                                                  </button>



                                             </div>
                                        </div>
                                   </div>
                              );
                         })}

                    </div>
               </div>
               {editingCitation && (
                    <EditCitationModal
                         citation={{ ...editingCitation, URLStatus: activeCitations.find(item => item.ID === editingCitation.ID)?.URLStatus || 'unverified' }}
                         onClose={finishEdit}
                         onDelete={finishDelete}
                    />
               )}
          </>
     );
};

export default CitationsList;
